import React, { useState } from 'react';
import { Box, Button } from '@mui/material';
import { WarningAmber as WarningIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AthleteActivityItem } from './athleteActivityStats';
import { AthleteActivitySplits } from './AthleteActivitySplits';

export type ReviewSuspiciousHandler = (activityId: string, dismissed: boolean) => Promise<void>;

interface AthleteActivityIntegrityProps {
  activity: AthleteActivityItem;
  onReview?: ReviewSuspiciousHandler;
}

const linkButtonSx = { minWidth: 0, p: '2px 6px', fontSize: 12, textTransform: 'none', color: 'var(--muted)' } as const;

// Admin-only block under an activity: flag reasons with dismiss/restore, and the km splits.
// Renders nothing for non-admins because the backend omits these fields for them.
export const AthleteActivityIntegrity = ({ activity, onReview }: AthleteActivityIntegrityProps) => {
  const { t } = useTranslation('contests');
  const [showSplits, setShowSplits] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const splits = activity.splits || [];

  if (!activity.suspicious && splits.length === 0) return null;

  const review = async (dismissed: boolean) => {
    if (!onReview) return;
    setSaving(true);
    setError(false);
    try {
      await onReview(activity.id, dismissed);
    } catch (err) {
      console.error('Failed to review suspicious activity:', err);
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  const dismissed = !!activity.suspiciousDismissed;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: '46px' }}>
      {activity.suspicious && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
            p: '8px 10px',
            borderRadius: '8px',
            fontSize: 12,
            backgroundColor: dismissed ? 'var(--muted-bg)' : 'var(--warn-bg)',
            color: dismissed ? 'var(--muted)' : 'var(--warn)',
          }}
        >
          <WarningIcon sx={{ fontSize: 16, mt: '1px' }} />
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Box component="span" sx={{ fontWeight: 600 }}>{dismissed ? t('athlete.integrity.dismissed') : t('athlete.integrity.flagged')}</Box>
            {(activity.suspiciousReasons || []).map(reason => (
              <Box component="span" key={reason} sx={{ textDecoration: dismissed ? 'line-through' : 'none' }}>{reason}</Box>
            ))}
            {error && <Box component="span" sx={{ color: 'var(--danger)' }}>{t('athlete.integrity.reviewError')}</Box>}
          </Box>
          {onReview && (
            <Button size="small" disabled={saving} onClick={() => review(!dismissed)} sx={{ ...linkButtonSx, color: 'inherit', fontWeight: 600 }}>
              {dismissed ? t('athlete.integrity.restore') : t('athlete.integrity.dismiss')}
            </Button>
          )}
        </Box>
      )}
      {splits.length > 0 && (
        <Box>
          <Button size="small" onClick={() => setShowSplits(v => !v)} aria-expanded={showSplits} sx={{ ...linkButtonSx, ml: '-6px' }}>
            {showSplits ? t('athlete.splits.hide') : t('athlete.splits.show', { count: splits.length })}
          </Button>
          {showSplits && <Box sx={{ mt: 0.75 }}><AthleteActivitySplits splits={splits} /></Box>}
        </Box>
      )}
    </Box>
  );
};
